/**
 * NAD+ Product Showcase Section
 * Handles product showcase interactions and animations
 */

class NADProductShowcase {
  private section: HTMLElement;
  private cards: NodeListOf<HTMLElement>;
  
  constructor(section: HTMLElement) {
    this.section = section;
    this.cards = this.section.querySelectorAll('.nad-product-showcase__card');
    this.init();
  }
  
  private init(): void {
    this.setupCardAnimations();
    this.setupScrollAnimation();
    console.log('NAD Product Showcase initialized');
  }

  private setupCardAnimations(): void {
    // Card animations are now handled by CSS hover effects
    // This method is kept for potential future JavaScript-based interactions
    console.log('Card animations setup complete - using CSS transitions');
  }

  /**
   * Setup scroll animation using Intersection Observer
   */
  private setupScrollAnimation(): void {
    if (!this.section) return;

    const showcaseCards = this.section.querySelectorAll('.nad-product-showcase__card');
    if (showcaseCards.length === 0) return;

    // Create intersection observer
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Add animation class when element becomes visible
            entry.target.classList.add('animate-in');
            // Stop observing this element once it's animated
            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.1, // Trigger when 10% of the element is visible
        rootMargin: '0px 0px -50px 0px' // Start animation slightly before element is fully visible
      }
    );

    // Observe all showcase cards
    showcaseCards.forEach((card) => {
      observer.observe(card);
    });

    // Store observer for cleanup
    (this.section as any).scrollObserver = observer;
  }

  /**
   * Cleanup method
   */
  public destroy(): void {
    // Cleanup intersection observer
    if (this.section && (this.section as any).scrollObserver) {
      (this.section as any).scrollObserver.disconnect();
      delete (this.section as any).scrollObserver;
    }
  }

  /**
   * Static method to initialize all sections
   */
  public static initializeAll(): NADProductShowcase[] {
    const sections = document.querySelectorAll<HTMLElement>('.nad-product-showcase');
    return Array.from(sections).map(section => new NADProductShowcase(section));
  }

  /**
   * Static method to destroy a section instance
   */
  public static destroySection(section: HTMLElement): void {
    const instance = (section as any).__nadProductShowcase;
    if (instance && instance.destroy) {
      instance.destroy();
    }
  }
}

// Export for initialization from main file
export { NADProductShowcase };
